import type { AuthSession, AuthenticatedUser } from '../types/auth.types';

/**
 * Treat a token as expired slightly early so a request issued just before the boundary
 * does not arrive at the server already invalid.
 */
const EXPIRY_SKEW_MS = 30_000;

/**
 * Holds the short-lived bearer token for the current page only.
 *
 * IdentityServer owns the durable session in an encrypted HttpOnly cookie. Keeping access
 * tokens out of Web Storage prevents an injected script from stealing a renewable session;
 * after a reload the app obtains a fresh access token from the cookie-backed refresh route.
 */
class TokenService {
  private cachedSession: AuthSession | null = null;

  save(session: AuthSession): void {
    this.cachedSession = session;
  }

  load(): AuthSession | null {
    return this.cachedSession;
  }

  getAccessToken(): string | null {
    const session = this.load();
    if (session === null) return null;
    return this.isExpired(session) ? null : session.accessToken;
  }

  isExpired(session: AuthSession, now: number = Date.now()): boolean {
    return session.expiresAt - EXPIRY_SKEW_MS <= now;
  }

  clear(): void {
    this.cachedSession = null;
  }
}

export const tokenService = new TokenService();

/** Converts the token endpoint's relative `expires_in` into an absolute timestamp. */
export function toAuthSession(
  response: {
    accessToken: string;
    expiresIn: number;
    tokenType?: string | null;
  },
  user: AuthenticatedUser,
  issuedAt: number = Date.now()
): AuthSession {
  return {
    accessToken: response.accessToken,
    expiresAt: issuedAt + response.expiresIn * 1000,
    tokenType: response.tokenType ?? 'Bearer',
    user,
  };
}
